import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, DollarSign, ArrowUpCircle, ArrowDownCircle, Filter } from 'lucide-react';
import { Card, Table, Select, Badge } from '../components/ui';
import { flujoCajaService } from '../services/api';
import { formatMoney, formatDate } from '../utils/formatters';
import { MESES } from '../utils/constants';
import toast from 'react-hot-toast';

const METODOS_PAGO = [
    { value: '', label: 'Todos' },
    { value: 'EFECTIVO', label: 'Efectivo' },
    { value: 'TRANSFERENCIA', label: 'Transferencia' },
    { value: 'YAPE', label: 'Yape' },
    { value: 'PLIN', label: 'Plin' },
    { value: 'CHEQUE', label: 'Cheque' },
    { value: 'TARJETA', label: 'Tarjeta' }
];

const TIPOS = [
    { value: '', label: 'Todos' },
    { value: 'INGRESO', label: 'Ingresos' },
    { value: 'EGRESO', label: 'Egresos' }
];

const FlujoCajaMovimientos = () => {
    const [movimientos, setMovimientos] = useState([]);
    const [saldoActual, setSaldoActual] = useState(0);
    const [resumen, setResumen] = useState({});
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        mes: new Date().getMonth() + 1,
        anio: new Date().getFullYear(),
        tipo: '',
        metodo_pago: ''
    });

    useEffect(() => {
        loadData();
    }, [filters]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [movimientosRes, saldoRes, resumenRes] = await Promise.all([
                flujoCajaService.getAll(filters),
                flujoCajaService.getSaldoActual(),
                flujoCajaService.getResumenMensual({ mes: filters.mes, anio: filters.anio })
            ]);

            setMovimientos(movimientosRes.data.data || []);
            setSaldoActual(saldoRes.data.data?.saldo_actual || 0);
            setResumen(resumenRes.data.data || {});
        } catch (error) {
            toast.error('Error al cargar datos');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            header: 'Fecha',
            render: (row) => <span className="text-slate-300">{formatDate(row.fecha)}</span>
        },
        {
            header: 'Tipo',
            render: (row) => (
                <Badge variant={row.tipo === 'INGRESO' ? 'success' : 'danger'}>
                    {row.tipo}
                </Badge>
            )
        },
        {
            header: 'Concepto',
            render: (row) => {
                let origen = '';

                if (row.tipo === 'INGRESO') {
                    // Para ingresos, mostrar departamento si existe
                    if (row.departamento_numero) {
                        origen = `Dpto ${row.departamento_numero}`;
                        if (row.departamento_propietario) {
                            origen += ` - ${row.departamento_propietario}`;
                        }
                    } else {
                        origen = row.concepto;
                    }
                } else {
                    // Para egresos, mostrar proveedor o personal
                    if (row.proveedor_nombre) {
                        origen = row.proveedor_nombre;
                    } else if (row.personal_nombre) {
                        origen = row.personal_nombre;
                    } else {
                        origen = row.concepto;
                    }
                }

                return (
                    <div>
                        <p className="text-white font-medium">{origen}</p>
                        <p className="text-xs text-slate-400">{row.categoria}</p>
                    </div>
                );
            }
        },
        {
            header: 'Método',
            render: (row) => <span className="text-slate-400">{row.metodo_pago || '-'}</span>
        },
        {
            header: 'Referencia',
            render: (row) => <span className="text-slate-400 font-mono text-xs">{row.referencia || '-'}</span>
        },
        {
            header: 'Ingreso',
            render: (row) => row.tipo === 'INGRESO' ? (
                <span className="font-semibold text-emerald-400 flex items-center gap-1">
                    <ArrowUpCircle size={14} />
                    {formatMoney(row.monto)}
                </span>
            ) : <span className="text-slate-600">-</span>
        },
        {
            header: 'Egreso',
            render: (row) => row.tipo === 'EGRESO' ? (
                <span className="font-semibold text-rose-400 flex items-center gap-1">
                    <ArrowDownCircle size={14} />
                    {formatMoney(row.monto)}
                </span>
            ) : <span className="text-slate-600">-</span>
        },
        {
            header: 'Saldo',
            render: (row) => (
                <span className={`font-bold ${row.saldo_nuevo >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {formatMoney(row.saldo_nuevo)}
                </span>
            )
        }
    ];

    return (
        <div className="space-y-6">
            {/* Estadísticas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-500/20">
                            <DollarSign className="text-blue-400" size={20} />
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">Saldo Actual</p>
                            <p className={`text-xl font-bold ${saldoActual >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                {formatMoney(saldoActual)}
                            </p>
                        </div>
                    </div>
                </Card>

                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-emerald-500/20">
                            <TrendingUp className="text-emerald-400" size={20} />
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">Ingresos del Mes</p>
                            <p className="text-xl font-bold text-emerald-400">
                                {formatMoney(resumen.total_ingresos || 0)}
                            </p>
                        </div>
                    </div>
                </Card>

                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-rose-500/20">
                            <TrendingDown className="text-rose-400" size={20} />
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">Egresos del Mes</p>
                            <p className="text-xl font-bold text-rose-400">
                                {formatMoney(resumen.total_egresos || 0)}
                            </p>
                        </div>
                    </div>
                </Card>

                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${(resumen.diferencia || 0) >= 0 ? 'bg-emerald-500/20' : 'bg-rose-500/20'}`}>
                            <DollarSign className={(resumen.diferencia || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'} size={20} />
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">Diferencia</p>
                            <p className={`text-xl font-bold ${(resumen.diferencia || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                {formatMoney(resumen.diferencia || 0)}
                            </p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Filtros */}
            <Card className="p-4">
                <div className="flex items-center gap-2 mb-4">
                    <Filter size={18} className="text-slate-400" />
                    <h3 className="font-semibold text-white">Filtros</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Select
                        label="Mes"
                        value={filters.mes}
                        onChange={(e) => setFilters({ ...filters, mes: e.target.value })}
                        options={MESES}
                    />
                    <Select
                        label="Año"
                        value={filters.anio}
                        onChange={(e) => setFilters({ ...filters, anio: e.target.value })}
                        options={[
                            { value: 2024, label: '2024' },
                            { value: 2025, label: '2025' },
                            { value: 2026, label: '2026' }
                        ]}
                    />
                    <Select
                        label="Tipo"
                        value={filters.tipo}
                        onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
                        options={TIPOS}
                    />
                    <Select
                        label="Método de Pago"
                        value={filters.metodo_pago}
                        onChange={(e) => setFilters({ ...filters, metodo_pago: e.target.value })}
                        options={METODOS_PAGO}
                    />
                </div>
            </Card>

            {/* Tabla de movimientos */}
            <Card className="overflow-hidden">
                <div className="p-4 border-b border-slate-700">
                    <h3 className="font-semibold text-white">Movimientos</h3>
                    <p className="text-sm text-slate-400">
                        {movimientos.length} {movimientos.length === 1 ? 'movimiento' : 'movimientos'} encontrados
                    </p>
                </div>
                <Table columns={columns} data={movimientos} loading={loading} />
            </Card>
        </div>
    );
};

export default FlujoCajaMovimientos;
